import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  WinningPatternData, 
  WinningPatternForm, 
  WinningPatternFilters, 
  GetWinningPatternsResponse 
} from '../../types';
import apiService from '../../services/api';

interface WinningPatternsState {
  patterns: WinningPatternData[];
  selectedPattern: WinningPatternData | null;
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  filters: WinningPatternFilters;
  loading: boolean;
  error: string | null;
}

const initialState: WinningPatternsState = {
  patterns: [],
  selectedPattern: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    pages: 0,
  },
  filters: {},
  loading: false,
  error: null,
};

// Async thunks
export const fetchWinningPatterns = createAsyncThunk(
  'winningPatterns/fetchWinningPatterns',
  async ({ page = 1, limit = 20, filters }: { page?: number; limit?: number; filters?: WinningPatternFilters }) => {
    const response = await apiService.getWinningPatterns(page, limit, filters);
    return response;
  }
);

export const fetchWinningPattern = createAsyncThunk(
  'winningPatterns/fetchWinningPattern',
  async (patternId: string) => {
    const response = await apiService.getWinningPattern(patternId);
    return response;
  }
);

export const createWinningPattern = createAsyncThunk(
  'winningPatterns/createWinningPattern',
  async (patternData: WinningPatternForm) => {
    const response = await apiService.createWinningPattern(patternData);
    return response;
  }
);

export const updateWinningPattern = createAsyncThunk(
  'winningPatterns/updateWinningPattern',
  async ({ patternId, patternData }: { patternId: string; patternData: Partial<WinningPatternForm> }) => {
    const response = await apiService.updateWinningPattern(patternId, patternData);
    return response;
  }
);

export const deleteWinningPattern = createAsyncThunk(
  'winningPatterns/deleteWinningPattern',
  async (patternId: string) => {
    const response = await apiService.deleteWinningPattern(patternId);
    return { ...response, patternId };
  }
);

const winningPatternsSlice = createSlice({
  name: 'winningPatterns',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedPattern: (state, action: PayloadAction<WinningPatternData | null>) => {
      state.selectedPattern = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
    setFilters: (state, action: PayloadAction<WinningPatternFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Patterns
      .addCase(fetchWinningPatterns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWinningPatterns.fulfilled, (state, action) => {
        state.loading = false;
        // Handle different response structures
        if (action.payload.success) {
          // If response has data array (for list endpoint)
          if (Array.isArray(action.payload.data)) {
            state.patterns = action.payload.data;
            state.pagination = {
              total: action.payload.pagination?.total || 0,
              page: action.payload.pagination?.page || 1,
              limit: action.payload.pagination?.limit || 20,
              pages: action.payload.pagination?.pages || 0,
            };
          }
          // If response has single pattern (for single endpoint)
          else if (action.payload.pattern) {
            state.patterns = [action.payload.pattern];
            state.pagination = {
              total: 1,
              page: 1,
              limit: 1,
              pages: 1,
            };
          }
          // If response has patterns array directly
          else if (Array.isArray(action.payload.patterns)) {
            state.patterns = action.payload.patterns;
            state.pagination = {
              total: action.payload.pagination?.total || action.payload.patterns.length,
              page: action.payload.pagination?.page || 1,
              limit: action.payload.pagination?.limit || 20,
              pages: action.payload.pagination?.pages || 1,
            };
          }
        }
      })
      .addCase(fetchWinningPatterns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch winning patterns';
      })
      // Fetch Single Pattern
      .addCase(fetchWinningPattern.fulfilled, (state, action) => {
        state.selectedPattern = action.payload;
      })
      // Create Pattern
      .addCase(createWinningPattern.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWinningPattern.fulfilled, (state, action) => {
        state.loading = false;
        state.patterns.unshift(action.payload);
      })
      .addCase(createWinningPattern.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create winning pattern';
      })
      // Update Pattern
      .addCase(updateWinningPattern.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWinningPattern.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.patterns.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.patterns[index] = action.payload;
        }
        if (state.selectedPattern?.id === action.payload.id) {
          state.selectedPattern = action.payload;
        }
      })
      .addCase(updateWinningPattern.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update winning pattern';
      })
      // Delete Pattern
      .addCase(deleteWinningPattern.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWinningPattern.fulfilled, (state, action) => {
        state.loading = false;
        state.patterns = state.patterns.filter(p => p.id !== action.payload.patternId);
        if (state.selectedPattern?.id === action.payload.patternId) {
          state.selectedPattern = null;
        }
      })
      .addCase(deleteWinningPattern.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete winning pattern';
      });
  },
});

export const { 
  clearError, 
  setSelectedPattern, 
  setPage, 
  setFilters, 
  clearFilters 
} = winningPatternsSlice.actions;

export default winningPatternsSlice.reducer; 